// LoginScreen — TagBoard web sign-in.
//
// Shown by the AuthGate in App.tsx when no session exists. After a successful
// signIn() the AuthProvider's onAuthStateChange handler flips the gate to
// render the main app — this screen unmounts on its own.
//
// Sign-up is admin-only in v1 (per product decision). Footer copy tells
// integrators to contact the administrator instead of providing a Sign Up
// link.

import React, {useState} from 'react'
import {FormField} from '../../components/common/FormField'
import {useAuth} from '../../app/useAuth'

export const LoginScreen: React.FC = () => {
  const {signIn} = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    const result = await signIn(email.trim(), password)
    setBusy(false)
    if (result.error) {
      setError(result.error)
    }
    // Success: AuthGate unmounts this screen; nothing to do here.
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-background">
      <form
        onSubmit={handleSubmit}
        className="flex w-[400px] flex-col gap-lg rounded-lg border border-surfaceBorder bg-surfaceCard p-xxl"
        aria-label="Sign in"
      >
        {/* Brand row — uses the same sidebar logo asset for visual continuity. */}
        <div className="flex items-center gap-md">
          <img
            src="/icons/logo.png"
            alt="TagBoard logo"
            className="h-11 w-11 rounded-md object-contain"
          />
          <div>
            <h1 className="text-xl font-bold text-textPrimary">TagBoard</h1>
            <p className="text-xs text-textMuted">Mining operations dispatcher</p>
          </div>
        </div>

        <FormField
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          placeholder="you@example.com"
        />
        <FormField
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          placeholder="••••••••"
        />

        {error && (
          <div
            role="alert"
            className="rounded-md border border-statusRed bg-badgeRedBg px-md py-sm text-sm text-statusRed"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email.trim() || !password}
          className="h-11 rounded-md bg-primary font-bold text-black transition-colors hover:bg-primaryLight disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="text-center text-xs text-textMuted">
          Need an account? Contact your administrator.
        </div>
      </form>
    </div>
  )
}
